/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PickerProvider = require('./PictProvider-InputType-Picker.js');

const libVocabularySelectorMap = require('./_VocabularySelectorMap.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'SampleStatusPicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
};

/**
 * Sample-status picker — the Picker-stack port of the legacy SampleStatusSelector (a Select-family
 * subclass; this class mirrors it 1:1 for diff-review).
 *
 * The vocabulary comes from the customer-managed `SampleStatus` table through the base broker's
 * vocabulary source (`_VocabularySelectorMap`), which commits the status CODE — what `Sample.Status`
 * stores — and falls back to a built-in list for a customer with no rows. On top of that this class
 * carries the legacy provider's cross-widget behavior: it derives a new sample status from the test
 * results reported on the event bus, seeds from a linked sample's status, and announces its own
 * changes as `SampleStatusChanged`.
 *
 * Tracking GUIDs use the SAME `MATERIALS-ENTITY-TRACKING-GUID-…` element ids as the legacy templates,
 * so a migrated field still binds to a still-legacy sibling Sample selector during a phased rollout.
 */
class PictProviderInputTypeSampleStatusPicker extends PickerProvider
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict} */
		this.pict;
	}

	getTrackingGUIDID(pInputHTMLID, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return `#MATERIALS-ENTITY-TRACKING-GUID-${pInputHTMLID}-${pRowIndex}`;
		}
		return `#MATERIALS-ENTITY-TRACKING-GUID-${pInputHTMLID}`;
	}

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput, 'SampleStatusSelector');
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput, 'SampleStatusSelector');
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {string} pEvent @param {string} pTransactionGUID @return {boolean}
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number} pRowIndex @param {string} pEvent @param {string} pTransactionGUID @return {boolean}
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * @param {Object} pView @param {Object} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		// The test-result event is bound to the TEST, so its tracking GUID cannot be validated against
		// the sample's here — the aggregate it triggers below is what carries the sample's GUID.
		if (tmpEventType === 'TestResultChanged')
		{
			if (pInput.PictForm?.AutoUpdate === false || pInput.PictForm?.AutoUpdate === 'false')
			{
				return;
			}
			this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(pTransactionGUID, () =>
			{
				if (pInput.PictForm.Disabled !== true)
				{
					this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), '__TestResultAggregate', []);
					this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestStatusAggregate:${this._getTrackingGUID(pInput, pRowIndex)}:__TestResultAggregate`);
				}
			});
		}

		if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}

		if (tmpEventType === 'TestStatusAggregate')
		{
			if (pInput.PictForm?.AutoUpdate === false || pInput.PictForm?.AutoUpdate === 'false')
			{
				return;
			}
			const tmpStatusGroup = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (!Array.isArray(tmpStatusGroup))
			{
				return;
			}
			const tmpCurrentStatus = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getValueAddress(pInput, pRowIndex));
			const tmpNewStatus = this._computeNewSampleStatusFromTestResults(tmpStatusGroup, tmpCurrentStatus);
			if (tmpNewStatus)
			{
				this._setValueAndCommit(pView, pInput, tmpNewStatus, pRowIndex);
			}
			return;
		}
		if (tmpEventType === 'SampleChanged')
		{
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (tmpSampleRecord && typeof tmpSampleRecord === 'object' && !Array.isArray(tmpSampleRecord) && tmpSampleRecord.Status)
			{
				const tmpCurrentStatus = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getValueAddress(pInput, pRowIndex));
				if (tmpCurrentStatus !== tmpSampleRecord.Status)
				{
					this._setValueAndCommit(pView, pInput, tmpSampleRecord.Status, pRowIndex);
				}
			}
			return;
		}
	}

	/**
	 * The marshal-relative address this field's status code lives at.
	 * @param {Object} pInput @param {number|string|null} [pRowIndex] @return {string}
	 */
	_getValueAddress(pInput, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
		}
		return pInput.DataAddress;
	}

	/**
	 * Derive a sample status from the statuses of its tests. A sample is never moved to a "higher"
	 * status automatically, and a manually-terminal status (voided / preauthorized / failed) is never
	 * overwritten.
	 *
	 * @param {Array<string>} pTestResultsArray @param {string} pSampleStatus @return {string | null}
	 */
	_computeNewSampleStatusFromTestResults(pTestResultsArray, pSampleStatus)
	{
		let tmpNewSampleStatus;
		let tmpSeenPendingTest = false;
		let tmpSeenFinalizedTest = false;
		let tmpSeenFailingTest = false;
		let tmpSeenPassingTest = false;
		for (const tmpTestResult of pTestResultsArray)
		{
			if (!tmpTestResult)
			{
				tmpSeenPendingTest = true;
				break;
			}
			if (tmpTestResult.startsWith('FAI'))
			{
				tmpSeenFailingTest = true;
				tmpSeenFinalizedTest = true;
			}
			if (tmpTestResult.startsWith('PAS'))
			{
				tmpSeenPassingTest = true;
				tmpSeenFinalizedTest = true;
			}
		}
		if (tmpSeenFailingTest)
		{
			tmpNewSampleStatus = 'FAIL';
			if (!pSampleStatus || pSampleStatus != 'VOID' && pSampleStatus != 'PREA' && pSampleStatus != 'FAIL' && pSampleStatus != 'FAIC')
			{
				this.pict.log.info(`SampleStatusPicker: updating sample status to ${tmpNewSampleStatus} based on test results.`, { pTestResultsArray, pSampleStatus });
				return tmpNewSampleStatus;
			}
		}
		else if (tmpSeenPendingTest)
		{
			tmpNewSampleStatus = tmpSeenFinalizedTest ? 'TEST' : 'PEND';
			if (!pSampleStatus || pSampleStatus != 'VOID' && pSampleStatus != 'PREA' && pSampleStatus != 'FAIL' && pSampleStatus != 'FAIC' && pSampleStatus != tmpNewSampleStatus)
			{
				this.pict.log.info(`SampleStatusPicker: updating sample status to ${tmpNewSampleStatus} based on test results.`, { pTestResultsArray, pSampleStatus });
				return tmpNewSampleStatus;
			}
		}
		else if (tmpSeenPassingTest)
		{
			tmpNewSampleStatus = 'PASS';
			if (!pSampleStatus || pSampleStatus == 'N/A' || pSampleStatus == 'LOGD' || pSampleStatus == 'RECV' || pSampleStatus == 'INFO' || pSampleStatus == 'PEND' || pSampleStatus == 'TEST' || pSampleStatus == 'TSTR')
			{
				this.pict.log.info(`SampleStatusPicker: updating sample status to ${tmpNewSampleStatus} based on test results.`, { pTestResultsArray, pSampleStatus });
				return tmpNewSampleStatus;
			}
		}
		return null;
	}

	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput, 'SampleStatusSelector');
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput, 'SampleStatusSelector');
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex);
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
		if (pInput.PictForm?.EmitStatusChange !== false)
		{
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`SampleStatusChanged:${this._getTrackingGUID(pInput)}:${pValue}`);
		}
		return tmpResult;
	}

	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpResult = super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (pInput.PictForm?.EmitStatusChange !== false)
		{
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`SampleStatusChanged:${this._getTrackingGUID(pInput, pRowIndex)}:${pValue}`);
		}
		return tmpResult;
	}

	/** @param {Object} pInput @param {number|string|null} [pRowIndex] @return {string} */
	_getTrackingGUID(pInput, pRowIndex)
	{
		if (pRowIndex == null && pInput.PictForm?.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			return pInput.PictForm.SampleTrackingGUID;
		}
		return /** @type {string} */ (this.pict.ContentAssignment.readContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex)));
	}

	/** @param {Object} pInput @param {number|string|null} pRowIndex @param {string} pTrackingGUID */
	_setTrackingGUID(pInput, pRowIndex, pTrackingGUID)
	{
		this.pict.ContentAssignment.assignContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex), pTrackingGUID);
	}

	/**
	 * Inherit the tracking GUID of the sample descriptor this status belongs to, so the sample's
	 * events reach this field. Searches the row's own group first (tabular), then the global manifest.
	 *
	 * @param {Object} pView @param {Object} pGroup @param {Object} pInput @param {number} [pRowIndex]
	 */
	_bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex)
	{
		if (this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}

		if (pRowIndex != null)
		{
			const tmpLocalManifest = pGroup.supportingManifest;
			if (!tmpLocalManifest || typeof tmpLocalManifest !== 'object' || Array.isArray(tmpLocalManifest))
			{
				this.pict.log.error('SampleStatusPicker: invalid supporting manifest on group.', { pGroup });
			}
			else
			{
				for (const tmpDescriptor of Object.values(tmpLocalManifest.elementDescriptors))
				{
					if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
					{
						const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, pRowIndex);
						if (tmpTrackingGUID)
						{
							this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
							return;
						}
					}
				}
			}
		}
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors || typeof tmpGlobalManifest.Descriptors !== 'object' || Array.isArray(tmpGlobalManifest.Descriptors))
		{
			this.pict.log.error('SampleStatusPicker: invalid global manifest on metacontroller.', { pGroup });
			return;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.SampleTrackingGUID)
			{
				const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, null);
				if (tmpTrackingGUID)
				{
					this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
					return;
				}
			}
		}
		if (!this._getTrackingGUID(pInput, pRowIndex))
		{
			this.pict.log.warn('SampleStatusPicker: could not find sibling sample descriptor to bind tracking GUID from.');
		}
	}
}

module.exports = PictProviderInputTypeSampleStatusPicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
